// Dialog for selecting a GIF session to add frames to
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { 
  Search, 
  Plus,
  X,
  Images,
  Clock,
  Folder,
  FolderPlus
} from 'lucide-react'
import { cn, formatFileSize } from '@/lib/utils'
import { GifSession } from '@/lib/local-types'

interface GifSessionSelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessions: GifSession[]
  isLoading: boolean
  onSelectSession: (session: GifSession) => void
  onCreateAndSelect: (name: string) => Promise<GifSession | null>
  title?: string
  description?: string
}

export function GifSessionSelector({
  open,
  onOpenChange,
  sessions,
  isLoading,
  onSelectSession,
  onCreateAndSelect,
  title = 'Add to GIF Session',
  description = 'Select an existing session or create a new one',
}: GifSessionSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewSession, setShowNewSession] = useState(false)
  const [newSessionName, setNewSessionName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setShowNewSession(false)
      setNewSessionName('')
    }
  }, [open])

  // Filter sessions by search query
  const filteredSessions = sessions.filter(session =>
    session.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return
    
    setIsCreating(true)
    try {
      const session = await onCreateAndSelect(newSessionName.trim())
      if (session) {
        onOpenChange(false)
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleSelectSession = (session: GifSession) => {
    onSelectSession(session)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Session list */}
          <ScrollArea className="h-[300px] rounded-md border">
            <div className="p-2 space-y-1">
              {/* Create new session option */}
              {showNewSession ? (
                <div className="p-3 space-y-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FolderPlus className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">New Session</span>
                  </div>
                  <Input
                    placeholder="Session name..."
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSession()
                      if (e.key === 'Escape') setShowNewSession(false)
                    }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowNewSession(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCreateSession}
                      disabled={isCreating || !newSessionName.trim()}
                    >
                      {isCreating ? 'Creating...' : 'Create & Add'}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewSession(true)}
                  className={cn(
                    'w-full text-left p-3 rounded-lg transition-colors',
                    'hover:bg-primary/10 border border-dashed border-primary/30',
                    'flex items-center gap-3'
                  )}
                >
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Plus className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-primary">Create New Session</p>
                    <p className="text-xs text-muted-foreground">Start a fresh GIF project</p>
                  </div>
                </button>
              )}

              {/* Existing sessions */}
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="p-3 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))
              ) : filteredSessions.length === 0 && !showNewSession ? (
                <div className="py-8 text-center">
                  {searchQuery ? (
                    <>
                      <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No sessions match "{searchQuery}"</p>
                    </>
                  ) : (
                    <>
                      <Folder className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No sessions yet</p>
                    </>
                  )}
                </div>
              ) : (
                filteredSessions.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    onClick={() => handleSelectSession(session)}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Individual session item
interface SessionItemProps {
  session: GifSession
  onClick: () => void
}

function SessionItem({ session, onClick }: SessionItemProps) {
  const updatedDate = new Date(session.updatedAt)
  const formattedDate = updatedDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: updatedDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  })

  // Get first frame thumbnail if available
  const thumbnailUrl = session.frames.length > 0 ? session.frames[0].url : null

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-3 rounded-lg transition-colors',
        'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        <div className="w-12 h-8 rounded bg-muted flex-shrink-0 flex items-center justify-center overflow-hidden">
          {thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt="" 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide broken image and show fallback icon
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <Images className="h-4 w-4 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Session name */}
          <p className="font-medium text-sm truncate">
            {session.name}
          </p>

          {/* Metadata */}
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Images className="h-3 w-3" />
              {session.frames.length} frames
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formattedDate}
            </span>
          </div>
        </div>

        {/* Size badge */}
        {session.totalSize > 0 && (
          <Badge variant="secondary" className="text-xs flex-shrink-0">
            {formatFileSize(session.totalSize)}
          </Badge>
        )}
      </div>
    </button>
  )
}
